import type { ModalOverlayProps as AriaModalOverlayProps } from 'react-aria-components/Modal';
import {
  DialogTrigger as AriaDialogTrigger,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
} from 'react-aria-components/Modal';

import { cx } from '../../../utils/cx';

export const DialogTrigger = AriaDialogTrigger;

/**
 * ModalOverlay - Full-screen backdrop for modal dialogs
 *
 * @note Z-Index: Currently using z-[1200] to match legacy navigation z-index values.
 * This is a temporary solution until a system-wide z-index cleanup is completed.
 * The navigation components (Header, Drawer, NavItems) use values ranging from 40-2001,
 * requiring modals to use 1200+ to appear above them.
 *
 * TODO: After navigation z-index cleanup, reduce this to z-50 to align with UI Kit standards:
 * - z-0/z-10: Basic layering
 * - z-40: Toaster/notifications
 * - z-50: Modals and overlays (target value)
 */
export const ModalOverlay = (props: AriaModalOverlayProps) => {
  return (
    <AriaModalOverlay
      {...props}
      className={(state) =>
        cx(
          'fixed inset-0 z-[1200] flex min-h-dvh w-full items-end justify-center overflow-y-auto bg-overlay/70 px-4 pt-4 pb-[clamp(16px,8vh,64px)] outline-hidden backdrop-blur-[6px] sm:items-center sm:justify-center sm:p-8',
          state.isEntering && 'duration-300 ease-out animate-in fade-in',
          state.isExiting && 'duration-200 ease-in animate-out fade-out',
          typeof props.className === 'function' ? props.className(state) : props.className,
        )
      }
    />
  );
};

export const Modal = (props: AriaModalOverlayProps) => (
  <AriaModal
    {...props}
    className={(state) =>
      cx(
        'max-h-full w-full align-middle outline-hidden max-sm:overflow-y-auto max-sm:rounded-xl',
        state.isEntering && 'duration-300 ease-out animate-in zoom-in-95',
        state.isExiting && 'duration-200 ease-in animate-out zoom-out-95',
        typeof props.className === 'function' ? props.className(state) : props.className,
      )
    }
  />
);

// Export modal parts
export { ModalContainer } from './modal-container';
export { ModalHeader } from './modal-header';
export type { ModalHeaderProps, ModalHeaderLayout } from './modal-header';
export { ModalContent } from './modal-content';
export { ModalFooter } from './modal-footer';
export type { ModalFooterProps, ModalFooterLayout } from './modal-footer';
